import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import {
    DKLabel,
    DKButton,
    DKInput,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    Toggle,
    DKRadioButton,
    DKTooltipWrapper,
    DKIcon,
    showAlert
} from "deskera-ui-library";
import ShiftManagementService from "../../services/ShiftManagementService";
import { fetchEmployeeListWithoutFilter, selectEmployeeListWithoutLimit } from "../../Redux/Slices/EmployeeListSlice";
import EmployeeListService from "../../services/EmployeeListService";
import { useDispatch, useSelector } from "react-redux";
import DKInputDateTime from "../common/DKInputDateTime";
import * as moment from "moment";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import ShiftUtils, { APPLICABLE_TO, MONTHLY_WHEN_CONSTANT, WEEK_DAYS, WEEK_DAY_STATUS, WEEK_DAY_STATUS_CONSTANTS } from "./ShiftUtils";
import { fetchSelectedShiftData, fetchShifts, selectedShiftSelector, selectShifts } from "../../Redux/Slices/ShiftManagementSlice";
import RouteManager, { PAGE_ROUTES } from "../../managers/RouteManager";
import AppManager from "../../managers/AppManager";
import MessageInfo from "../common/MessageInfo";
import Utility, { removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import { getQueryParam } from "../../managers/common/CommonUtil";
import ic_info_message from "../../assets/icons/ic_info_gray01.png";

function AddShift(props) {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const shiftDetails = useSelector(selectedShiftSelector);

    const isViewMode = getQueryParam(props.location, "isViewMode") === "true";
    const shiftId = getQueryParam(props.location, "id")
    const [defaultFromAndToTime] = useState(ShiftUtils.getDefaultFromAndToTime(TenantService.get(TENANT.TENANT_WORK_WEEK)));
    const [defaultShiftMarginTime] = useState(ShiftUtils.getDefaultShiftMarginTime());
    const [requiredPresentHrs] = useState(ShiftUtils.getRequiredPresentHrs(defaultFromAndToTime.from, defaultFromAndToTime.to));

    const initialFormState = {
        name: {
            ref: React.createRef(),
            id: "name",
            title: t("SHIFT_NAME"),
            type: INPUT_TYPE.TEXT,
            validation: [["required"], ["duplicate"]],
            validator: (value) => {
                shiftNameValidator(value);
            }
        },
        from: {
            id: "from",
            title: t("FROM"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: defaultFromAndToTime.from
        },
        to: {
            id: "to",
            title: t("TO"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: defaultFromAndToTime.to
        },
        shiftMargin: {
            id: "shiftMargin",
            title: t("SHIFT_MARGIN"),
            type: "radio",
            validation: [["required"]],
            options: [{ value: true, label: "Enable" }, { value: false, label: "Disable" }],
            value: false
        },
        shiftMarginBefore: {
            id: "shiftMarginBefore",
            title: t("SHIFT_MARGIN_BEFORE"),
            type: "date-time-input",
            is12Hour: false,
            hideFooterDateTime: true,
            value: defaultShiftMarginTime.before,
            readOnly: true
        },
        shiftMarginAfter: {
            id: "shiftMarginAfter",
            title: t("SHIFT_MARGIN_AFTER"),
            type: "date-time-input",
            is12Hour: false,
            hideFooterDateTime: true,
            value: defaultShiftMarginTime.after,
            readOnly: true
        },
        reqdHrsToMarkPresent: {
            id: "reqdHrsToMarkPresent",
            title: t("REQUIRED_HRS_FOR_FULL_DAY"),
            type: "date-time-input",
            validation: [["required"], ["min", requiredPresentHrs.half], ["max", requiredPresentHrs.full]],
            is12Hour: false,
            hideFooterDateTime: true,
            value: requiredPresentHrs.full
        },
        reqdHrsToMarkHalfPresent: {
            id: "reqdHrsToMarkHalfPresent",
            title: t("REQUIRED_HRS_FOR_HALF_DAY"),
            type: "date-time-input",
            validation: [["required"], ["min", requiredPresentHrs.half], ["max", requiredPresentHrs.full]],
            is12Hour: false,
            hideFooterDateTime: true,
            value: requiredPresentHrs.half
        },
        applicableTo: {
            id: "applicableTo",
            title: t("APPLICABLE_TO_USERS"),
            type: "radio",
            options: [
                { value: APPLICABLE_TO.NONE, label:  t("NONE") },
                { value: APPLICABLE_TO.ALL, label: t("ALL") },
                { value: APPLICABLE_TO.SELECTED, label: t("SELECTED") }
            ],
            validation: [["required"]],
            value: "none"
        },
        employees: {
            id: "employees",
            title: t("USERS"),
            type: INPUT_TYPE.MULTI_SELECT,
            readOnly: true
        },
        isMonthlyShift: {
            id: "isMonthlyShift",
            title: "Enable Monthly shifts",
            type: 'toggle',
            readOnly: false
        },
        exemptFromCompanyHolidays: {
            id: "exemptFromCompanyHolidays",
            title: "Exempt From Company Holiday",
            type: 'toggle',
            readOnly: false
        }        
    }

    const shifts = useSelector(selectShifts);
    const employeeResponse = useSelector(selectEmployeeListWithoutLimit);

    const [formState, setFormState] = useState(initialFormState);
    const [employeeOptions, setEmployeeOptions] = useState(null);
    const [shift, setShift] = useState(null);

    const [shiftWorkWeek, setShiftWorkWeek] = useState(WEEK_DAYS);
    const [isShiftView, setIsShiftView] = useState(isViewMode);
    const [selectedShiftUserId, setSelectedShiftUserId] = useState(null);

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();

        fetchDependencies();
        if(!Utility.isEmpty(shiftId) && isShiftView && Utility.isEmpty(ShiftManagementService.SHIFT)) {
            fetchShiftDetails(shiftId)
        }
        if (ShiftManagementService.SHIFT) {
            setShift(ShiftManagementService.SHIFT);
            ShiftManagementService.SHIFT = null;

            if (ShiftManagementService.IS_SHIFT_VIEW) {
                setIsShiftView(true);
                ShiftManagementService.IS_SHIFT_VIEW = false;
            }

            if(!Utility.isEmpty(ShiftManagementService.SELECTED_SHIFT_USER_ID)) {
                setSelectedShiftUserId(ShiftManagementService.SELECTED_SHIFT_USER_ID);
                ShiftManagementService.SELECTED_SHIFT_USER_ID = null;
            }
        } else {
            setShift(null);
            enrichShiftWorkWeek(TenantService.get(TENANT.TENANT_WORK_WEEK), false);
        }
    }, []);

    useEffect(() => {
        if (employeeOptions && shift) {
            setShiftData(shift);
        }
    }, [employeeOptions, shift]);

    useEffect(() => {
        if (employeeOptions !== null) {
            const newFormState = {...formState};
            newFormState.employees.options = Array.isArray(employeeOptions) ? employeeOptions : [];
            setFormState(newFormState);
        }
    }, [employeeOptions]);

    useEffect(() => {
        if (employeeResponse?.users?.length > 0) {
            const users = ShiftUtils.getMembersWithoutShift(shifts, employeeResponse.users, shift?.id);
            setEmployeeOptions(users);
        }
    }, [shift, employeeResponse, shifts]);

    useEffect(() => {
        if (isShiftView) {
            if (formState.name.ref) {
                const input = document.getElementById(formState.name.ref?.current?.textfieldId);
                if (input) {
                    input.tabIndex = -1;
                }
            }
        }
    }, [isShiftView]);

    useEffect(() => {
        if(isShiftView && !Utility.isEmptyObject(shiftDetails)) {
            setShift(shiftDetails);
        }
    },[shiftDetails])

    const fetchDependencies = async () => {
        EmployeeListService.apiConfig = {
            customJson: {
                "orderBy.field": "updatedAt",
                "orderBy.order": "desc"
            }
        };

        const timeout = showDelayedLoader();
        await Promise.all([dispatch(fetchEmployeeListWithoutFilter()), dispatch(fetchShifts())]);
        removeDelayedLoader(timeout);
    }

    const fetchShiftDetails = async (shiftId) => {
        try{
            const timeout = showDelayedLoader();
            await dispatch(fetchSelectedShiftData(shiftId));
            removeDelayedLoader(timeout);
        }catch(err) { }
    }

    const shiftNameValidator = (value) => {
        return !ShiftUtils.isDuplicateShiftName(String(value).trim(), shift, shifts);
    }

    const enrichShiftWorkWeek = (weekDays, isShiftWorkWeek) => {
        if (Array.isArray(weekDays)) {
            const workWeek = [...shiftWorkWeek];
            workWeek.forEach((workDay) => {
                const weekDay = weekDays.find((wwd) => String(wwd.day).toLowerCase() ===  String(workDay.id).toLowerCase());
                if (isShiftWorkWeek) {
                    workDay.inTime.value = moment(weekDay.inTime, "HH:mm");
                    workDay.outTime.value = moment(weekDay.outTime, "HH:mm");
                    workDay.workingStatus.value = getDropdownLabel(weekDay.workingStatus, workDay.workingStatus.options);
                } else {
                    if (weekDay?.isWorkingDay) {
                        workDay.inTime.value = moment(weekDay.startTime, "hh:mm A");
                        workDay.outTime.value = moment(weekDay.endTime, "hh:mm A");
                        workDay.workingStatus.value = "Regular Day";
                    } else {
                        workDay.inTime.value = moment("00:00", "hh:mm");
                        workDay.outTime.value = moment("00:00", "hh:mm");
                        workDay.workingStatus.value = "Off Day";
                    }
                }
                if(formState.isMonthlyShift.value) {
                    workDay.when.value = getDropdownLabel(weekDay.when, workDay.when.options);
                } else {
                    workDay.when.value = getDropdownLabel('All', workDay.when.options);
                }

            });

            setShiftWorkWeek(workWeek);
        }
    }

    const setShiftData = (shift) => {
        const state = {...formState};

        state.name.value = shift.name;
        state.isMonthlyShift.value = shift.isMonthlyShift ? true:false;
        state.exemptFromCompanyHolidays.value = shift.exemptFromCompanyHolidays ? true:false;
        
        if (shift.from) {
            state.from.value = moment(shift.from, "HH:mm");
        }

        if (shift.to) {
            state.to.value = moment(shift.to, "HH:mm");
        }

        state.shiftMargin.value = shift.shiftMargin;

        if (shift.shiftMargin) {
            if (shift.shiftMarginBefore) {
                state.shiftMarginBefore.value =  moment(shift.shiftMarginBefore, "HH:mm");
            }

            if (shift.shiftMarginAfter) {
                state.shiftMarginAfter.value = moment(shift.shiftMarginAfter, "HH:mm");
            }
        }

        if (shift.reqdHrsToMarkPresent) {
            state.reqdHrsToMarkPresent.value = moment(shift.reqdHrsToMarkPresent, "HH:mm");
        }

        if (shift.reqdHrsToMarkHalfPresent) {
            state.reqdHrsToMarkHalfPresent.value = moment(shift.reqdHrsToMarkHalfPresent, "HH:mm");
        }

        if (shift.applicableTo) {
            state.applicableTo.value = APPLICABLE_TO.ALL;
        } else {
            if (shift.employees?.length > 0) {
                state.applicableTo.value = APPLICABLE_TO.SELECTED;
                state.employees.value = getMultiSelectDropdownSelectedIndexes(shift.employees, employeeOptions);
            } else {
                state.applicableTo.value = APPLICABLE_TO.NONE;
            }
        }

        // updateValueAndValidity(state, "to");
        updateValueAndValidity(state, "shiftMargin");
        updateValueAndValidity(state, "reqdHrsToMarkPresent");
        updateValueAndValidity(state, "reqdHrsToMarkHalfPresent");
        updateValueAndValidity(state, "applicableTo");

        setTimeout(() => {
            setFormState(state);
        }, 10);

        if (shift.shiftWorkWeek?.length > 0) {
            enrichShiftWorkWeek(shift.shiftWorkWeek, true);
        } else {
            enrichShiftWorkWeek(TenantService.get(TENANT.TENANT_WORK_WEEK), false);
        }
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const getMultiSelectDropdownSelectedIndexes = (v, o) => {
        const value = [];

        if (Array.isArray(v)) {
            v.forEach((i) => {
                const index = o.findIndex((item) => item.value === i.employeeId);
                if (index !== -1) {
                    value.push(index);
                }
            });
        }

        return value;
    }

    const getMultiSelectDropdownValue = (v, o) => {
        const value = [];

        if (Array.isArray(v)) {
            v.forEach((i) => {
                const option = o.find((item, index) => index === i);
                if (option) {
                    value.push(option.value);
                }
            });
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <div style={{
                    marginTop: "-19px",
                    marginBottom: "8px"
                }}>
                    <DKLabel
                        className="ml-xs fs-s text-red"
                        text={errorMessage}
                    />
                </div>
            )
        }
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "min":
                        {
                            if (value instanceof moment && validation[1] instanceof moment) {
                                const valueTotalMinutes = value.get("hours") * 60 + value.get("minutes");
                                const validatorTotalMinutes = validation[1].get("hours") * 60 + validation[1].get("minutes");
                                if (valueTotalMinutes < validatorTotalMinutes) {
                                    error.invalid = true;
                                    switch (field.id) {
                                        case "reqdHrsToMarkPresent":
                                            error.errorMessage = "Required hrs cannot be less than 1/2 day required hrs";
                                            break;
                                        case "reqdHrsToMarkHalfPresent":
                                            error.errorMessage = "Required 1/2 day hrs cannot be less than 1/2 of full day hrs";
                                            break;
                                        default:
                                            break;
                                    }
                                }
                            }
                        }
                        break;
                    case "max":
                        {
                            if (value instanceof moment && validation[1] instanceof moment) {
                                const valueTotalMinutes = value.get("hours") * 60 + value.get("minutes");
                                const validatorTotalMinutes = validation[1].get("hours") * 60 + validation[1].get("minutes");
                                if (valueTotalMinutes > validatorTotalMinutes) {
                                    error.invalid = true;
                                    switch (field.id) {
                                        case "shiftMarginBefore":
                                        case "shiftMarginAfter":
                                            error.errorMessage = "Margin hrs cannot be more than 1/2 day required hrs";
                                            break;
                                        case "reqdHrsToMarkPresent":
                                            error.errorMessage = "Required hrs cannot be more than shift duration";
                                            break;
                                        default:
                                            break;
                                    }
                                }
                            }
                        }
                        break;
                    case "duplicate":
                        if (ShiftUtils.isDuplicateShiftName(String(value).trim(), shift, shifts)) {
                            error.invalid = true;
                            error.errorMessage = "Duplicate shift name is not allowed";
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, field, value, state) => {
        switch (stateName) {
            case "shiftWorkWeek":
                const workWeek = [...shiftWorkWeek];
                workWeek.forEach((workDay) => {
                    if (workDay.id === state.id) {
                        workDay[field.id].value = value;
                    }
                });
                setShiftWorkWeek(workWeek);
                break;
            default:
                const fState = {...formState};

                if ((stateName === "from" || stateName === "to") && moment(fState[stateName].value).format("HH:mm") !== moment(value).format("HH:mm")) {
                    showUpdateShiftWorkWeekAlert(stateName, value);
                }

                fState[stateName] = {...field, value: value, ...hasError(field, value)};
                updateValueAndValidity(fState, stateName);
                setFormState(fState);
                break;
        }
    }

    const updateValidity = (state, stateName) => {
        state[stateName] = {...state[stateName], ...hasError(state[stateName], state[stateName].value)};
    }

    const updateValueAndValidity = (state, stateName) => {
        const requiredPresentHrs = ShiftUtils.getRequiredPresentHrs(state.from.value, state.to.value);

        switch (stateName) {
            case "from":
            case "to":
                state.reqdHrsToMarkPresent.value = requiredPresentHrs.full;
                state.reqdHrsToMarkHalfPresent.value = requiredPresentHrs.half;
                state.reqdHrsToMarkPresent.validation = [["required"], ["min", requiredPresentHrs.half], ["max", requiredPresentHrs.full]];
                state.reqdHrsToMarkHalfPresent.validation = [["required"], ["min", requiredPresentHrs.half], ["max", requiredPresentHrs.full]];
                if (state.shiftMargin.value) {
                    state.shiftMarginBefore.validation = [["required"], ["max", requiredPresentHrs.half]];
                    state.shiftMarginAfter.validation = [["required"], ["max", requiredPresentHrs.half]];
                    updateValidity(state, "shiftMarginBefore");
                    updateValidity(state, "shiftMarginAfter");
                }    
                updateValidity(state, "reqdHrsToMarkPresent");
                updateValidity(state, "reqdHrsToMarkHalfPresent");
               
                break;
            case "shiftMargin":
                const validation = [["required"], ["max", requiredPresentHrs.half]];
                state.shiftMarginBefore.validation = state.shiftMargin.value ? validation : [];
                state.shiftMarginAfter.validation = state.shiftMargin.value ? validation : [];
                state.shiftMarginBefore.readOnly = !state.shiftMargin.value;
                state.shiftMarginAfter.readOnly = !state.shiftMargin.value;

                updateValidity(state, "shiftMarginBefore");
                updateValidity(state, "shiftMarginAfter");
                break;
            case "reqdHrsToMarkPresent":
                const halfDayHrs = ShiftUtils.getHalfDayHrsForGivenFullDay(state.reqdHrsToMarkPresent.value);
                state.reqdHrsToMarkHalfPresent.validation = [["required"], ["min", halfDayHrs || requiredPresentHrs.half], ["max", requiredPresentHrs.full]];

                updateValidity(state, "reqdHrsToMarkHalfPresent");
                break;
            case "reqdHrsToMarkHalfPresent":
                state.reqdHrsToMarkPresent.validation = [["required"], ["min", state.reqdHrsToMarkHalfPresent.value], ["max", requiredPresentHrs.full]];
                if (state.shiftMargin.value) {
                    state.shiftMarginBefore.validation = [["required"], ["max", state.reqdHrsToMarkHalfPresent.value]];
                    state.shiftMarginAfter.validation = [["required"], ["max", state.reqdHrsToMarkHalfPresent.value]];
                    updateValidity(state, "shiftMarginBefore");
                    updateValidity(state, "shiftMarginAfter");
                }
                updateValidity(state, "reqdHrsToMarkPresent");             
                break;
            case "applicableTo":
                state.employees.validation = state.applicableTo.value === APPLICABLE_TO.SELECTED ? [["required"]] : [];
                state.employees.readOnly = state.applicableTo.value !== APPLICABLE_TO.SELECTED;
            default:
                break;
        }
    }

    const showUpdateShiftWorkWeekAlert = (stateName, value) => {
        showAlert(t("ALERT"), `${stateName === "from" ? t("UPDATE_FROM_ALERT") : t("UPDATE_TO_ALERT")}`,
            [
                { title: t("YES"), className: "bg-app mr-m text-white", onClick: () => { updateShiftWorkWeek(stateName, value) }},
                { title: t("NO"), onClick: () => { }}
            ]
        );
    }

    const updateShiftWorkWeek = (stateName, value) => {
        const workWeek = [...shiftWorkWeek];
        workWeek.forEach((workDay) => {
            if (workDay.workingStatus.value === "Regular Day") {
                if (stateName === "from") {
                    workDay.inTime.value = moment(value, "HH:mm");
                } else {
                    workDay.outTime.value = moment(value, "HH:mm");
                }
            }
        });

        setShiftWorkWeek(workWeek);
    }

    const isInvalid = () => {
        let invalid = false;

        const state = {...formState};
        Object.entries(state).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        let isValidStatus = false;
        shiftWorkWeek.forEach((workDay) => {
          const workingStatus = getDropdownValue(
            workDay.workingStatus.value,
            workDay.workingStatus.options
          );
          switch (workingStatus) {
            case WEEK_DAY_STATUS_CONSTANTS.WEEK_DAY:
            case WEEK_DAY_STATUS_CONSTANTS.HALF_DAY:
              isValidStatus = true;
              break;
            default:
              break;
          }
          if (!isValidStatus && formState.isMonthlyShift?.value) {
            const whenStatus = getDropdownValue(
                workDay.when.value,
                workDay.when.options
            );
            switch (whenStatus) {
              case MONTHLY_WHEN_CONSTANT.ALTERNATE_EVEN_WEEK:
              case MONTHLY_WHEN_CONSTANT.ALTERNATE_ODD_WEEK:
              case MONTHLY_WHEN_CONSTANT.FIRST_WEEK:
              case MONTHLY_WHEN_CONSTANT.SECOND_WEEK:
              case MONTHLY_WHEN_CONSTANT.THIRD_WEEK:
              case MONTHLY_WHEN_CONSTANT.FOURTH_WEEK:
              case MONTHLY_WHEN_CONSTANT.FIFTH_WEEK:
              case MONTHLY_WHEN_CONSTANT.SIXTH_WEEK:
                isValidStatus = true;
                break;
              default:
                break;
            }
          }
        });
        
        if (!isValidStatus) {
            showToast(
              "Please select atleast one working day in status",
              TOAST_TYPE.FAILURE
            );
            invalid = true;
          
        }


        setFormState(state);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            name: String(formState.name.value).trim(),
            from: ShiftUtils.get24HrsTime(formState.from.value),
            to: ShiftUtils.get24HrsTime(formState.to.value),
            shiftMargin: formState.shiftMargin.value,
            shiftMarginBefore: formState.shiftMargin.value ? ShiftUtils.get24HrsTime(formState.shiftMarginBefore.value) : null,
            shiftMarginAfter: formState.shiftMargin.value ? ShiftUtils.get24HrsTime(formState.shiftMarginAfter.value) : null,
            reqdHrsToMarkPresent: ShiftUtils.get24HrsTime(formState.reqdHrsToMarkPresent.value),
            reqdHrsToMarkHalfPresent: ShiftUtils.get24HrsTime(formState.reqdHrsToMarkHalfPresent.value),
            isMonthlyShift: Boolean(formState.isMonthlyShift?.value),
            exemptFromCompanyHolidays: Boolean(formState.exemptFromCompanyHolidays?.value)
        }

        if (shift) {
            payload.id = shift.id;
        }

        switch (formState.applicableTo.value) {
            case APPLICABLE_TO.SELECTED:
                payload.applicableTo = false;
                payload.employees = getMultiSelectDropdownValue(formState.employees.value, formState.employees.options);
                break;
            case APPLICABLE_TO.NONE:
                payload.applicableTo = false;
                payload.employees = [];
                break;
            default:
                payload.applicableTo = true;
                payload.employees = null;
                break;
        }

        if (Array.isArray(shiftWorkWeek)) {
            payload.shiftWorkWeek = shiftWorkWeek.map((workDay) => {
                let workObject = {
                    day: workDay.id,
                    workingStatus: getDropdownValue(workDay.workingStatus.value, workDay.workingStatus.options),
                    inTime: ShiftUtils.get24HrsTime(workDay.inTime.value),
                    outTime: ShiftUtils.get24HrsTime(workDay.outTime.value)
                }
                if(formState.isMonthlyShift?.value) {
                    workObject = {
                        ...workObject,
                        when: getDropdownValue(workDay.when.value, workDay.when.options),
                    }
                }
                return workObject
            });
        }

        return payload;
    }

    const saveShift = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();

        if (ShiftUtils.isShiftWithMembersExist(shifts, payload)) {
            showAlert(t("ALERT"), t("DUPLICATE_SHIFT_MEMBER"));
            return;
        }

        if (shift) {
            showLoader(t("UPDATING_SHIFT"));
            ShiftManagementService.updateShift(payload).then(
                (response) => {
                    removeLoader();
                    if (response) {
                        showToast(t("SHIFT_UPDATE_SUCCESS"), TOAST_TYPE.SUCCESS);
                        RouteManager.navigateToPage(PAGE_ROUTES.SHIFTS);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }, (error) => {
                    removeLoader();
                    if (error?.message) {
                        if (error.message === "shift with given name already exists") {
                            showToast("Duplicate shift name is not allowed", TOAST_TYPE.FAILURE);
                        } else {
                            showToast(error.message, TOAST_TYPE.FAILURE);
                        }
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader(t("SAVING_SHIFT"));
            ShiftManagementService.addShift(payload).then(
                (response) => {
                    removeLoader();
                    if (response) {
                        showToast(t("SHIFT_SAVE_SUCCESS"), TOAST_TYPE.SUCCESS);
                        RouteManager.navigateToPage(PAGE_ROUTES.SHIFTS);
                    }  else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }, (error) => {
                    removeLoader();
                    if (error?.message) {
                        if (error.message === "shift with given name already exists") {
                            showToast("Duplicate shift name is not allowed", TOAST_TYPE.FAILURE);
                        } else {
                            showToast(error.message, TOAST_TYPE.FAILURE);
                        }
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        }
    }

    const routeToPage = () => {
        let tenantData = TenantService.getData()
        const isAdmin = tenantData['IS_ADMIN']
        if(isShiftView && isAdmin && !Utility.isEmpty(selectedShiftUserId)) {
            RouteManager.navigateToPage(
                PAGE_ROUTES.EMPLOYEE_VIEW,
                `id=${selectedShiftUserId}`
              );
        } else if(isShiftView && !isAdmin) {
            RouteManager.navigateToHome()
        } else {
            RouteManager.navigateToPage(PAGE_ROUTES.SHIFTS)
        }
    }

    const renderField = (stateName, field, width, state, className) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                ref={field.ref}
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || isShiftView}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "warning-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value, state);
                                }}
                                validator={field.validator}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {},
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || isShiftView}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value, state);
                                }}
                            />
                        </div>
                        {field.invalid && showError(field.errorMessage)}
                    </div>
                );
            case "date-time-input":
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInputDateTime
                                className="parent-width"
                                is12Hour={field.is12Hour}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                title={isRequired(field.validation) ? `${field.title} *` : field.title}
                                hideLabel={!!field.hideLabel}
                                hideCalendar={true}
                                hideFooterDateTime={field.hideFooterDateTime}
                                selectedDate={moment(field.value, "hh:mm A").format()}
                                readOnly={!!field.readOnly || isShiftView}
                                errorMessage={field.errorMessage}
                                invalid={field.invalid}
                                onSave={(date) => {
                                    updateState(stateName, field, moment(date), state);
                                }}
                            />
                        </div>
                        {field.invalid && showError(field.errorMessage)}
                    </div>
                );
            case "toggle":
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l " : className}`}>
                            <DKLabel  className=" mr-r" text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                            <Toggle
                                style={{ width: "20%" }}
                                isOn={field.value}
                                onChange={() => {
                                    updateState(stateName, field, !field.value, state);
                                }}
                            />
                        </div>
                    </div>
                );
            case "radio":
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }} className="pb-l mb-s">
                        <DKLabel className="mb-m " text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                        <div className="d-flex row-responsive">
                            {field.options.map(button => {
                                return (
                                    <DKRadioButton
                                        title={button.label}
                                        color="bg-app"
                                        className={"mr-m mb-s"}
                                        isSelected={field.value === button.value}
                                        disabled={!!field.readOnly || isShiftView}
                                        onClick={() => {
                                            updateState(stateName, field, button.value, state);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `${width}`, minWidth:width, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                dropdownConfig={{
                                    data: field.options,
                                    renderer: (i, item) => {
                                        return <div className="row">
                                            <DKLabel text={item.label} />

                                            {item.displayMsg && <DKTooltipWrapper
                                                content={item.displayMsg}
                                                tooltipClassName="warning-tooltip-prop warning-tooltip-prop "
                                            >
                                                <DKIcon
                                                    src={ic_info_message}
                                                    className="ml-s ic-s ml-r mt-xs cursor-hand "
                                                    style={{ opacity: 0.4 }}
                                                />
                                            </DKTooltipWrapper>}
                                        </div>
                                    },
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label, state);
                                    }
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || isShiftView}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    const renderShiftWorkDay = (day) => {
        return (
            <tr>
                <td>{day.name}</td>
                <td className="pt-m">
                    {renderField("shiftWorkWeek", day.inTime, "90%", day, "mb-xs")}
                </td>
                <td className="pt-m">
                    {renderField("shiftWorkWeek", day.outTime, "90%", day, "mb-xs")}
                </td>
                {formState.isMonthlyShift?.value && <td className="pt-m" style={{width: 232}}>
                    {renderField("shiftWorkWeek", day.when, "90%", day, "mb-xs")}
                </td>}
                <td className="pt-m">
                    {renderField("shiftWorkWeek", day.workingStatus, "100%", day, "mb-xs")}
                </td>
            </tr>
        );
    }

    const renderShiftWorkWeek = () => {
        return (
            <table className="parent-width">
                <tr>
                    <th className="shift-weekday-header-cell p-v-m">
                        Day
                    </th>
                    <th className="shift-weekday-header-cell p-v-m">
                        From
                    </th>
                    <th className="shift-weekday-header-cell p-v-m">
                        To
                    </th>
                    {formState.isMonthlyShift?.value && <th className="shift-weekday-header-cell p-v-m">
                        When
                    </th>}
                    <th className="shift-weekday-header-cell p-v-m">
                        Status
                    </th>
                </tr>
                {shiftWorkWeek.map((day) => renderShiftWorkDay(day))}
            </table>
        );
    }

    return (
        <div className="parent-width">
            <div className="row mb-s">
                {!isShiftView && <DKLabel text={shift ? t("EDIT_SHIFT") : t("ADD_SHIFT")} className="fs-l fw-b" />}
                {isShiftView && <DKLabel text={t("ASSIGNED_SHIFT")} className="fs-l fw-b" />}
                <div className="ml-auto d-flex">
                    <DKButton
                        title={t("CANCEL")}
                        className="border-radius-m bg-gray1 border-m"
                        onClick={() => { routeToPage() }}
                    />
                    {!isShiftView &&
                        <DKButton
                            title={t("SAVE")}
                            className="bg-app text-white ml-m"
                            onClick={() => {
                                saveShift();
                            }}
                        />
                    }
                </div>
            </div>
            {!isShiftView && <MessageInfo message={[{ message: t("SHIFT_MSG_TEXT") }]} bannerClass="mb-s" />}
            <div className="bg-white border-radius-m p-h-xxl p-v-xl">
                <div className="row justify-content-between align-items-start">
                    {renderField("name", formState.name, "47%")}
                    {renderField("shiftMargin", formState.shiftMargin, "47%")}
                </div>
                <div className="row justify-content-between">
                    {renderField("from", formState.from, "47%")}
                    {renderField("shiftMarginBefore", formState.shiftMarginBefore, "47%")}
                </div>
                <div className="row justify-content-between">
                    {renderField("to", formState.to, "47%")}
                    {renderField("shiftMarginAfter", formState.shiftMarginAfter, "47%")}
                </div>
                <div className="row justify-content-between align-items-start">
                    {renderField("reqdHrsToMarkPresent", formState.reqdHrsToMarkPresent, "47%")}
                    {!isShiftView && renderField("applicableTo", formState.applicableTo, "47%")}
                </div>
                <div className="row justify-content-between">
                    {renderField("reqdHrsToMarkHalfPresent", formState.reqdHrsToMarkHalfPresent, "47%")}
                    {!isShiftView && renderField("employees", formState.employees, "47%")}
                </div>
                <div className="row justify-content-between">
                    
                    {!isShiftView && renderField("isMonthlyShift", formState.isMonthlyShift, "47%")}
                    {!isShiftView && renderField("exemptFromCompanyHolidays", formState.exemptFromCompanyHolidays, "47%")}
                </div>
                <div className="mt-l">
                    {renderShiftWorkWeek()}
                </div>
            </div>
        </div>
    );
}

export default AddShift;
